/**
 * The reveal, as arithmetic.
 *
 * `draft-order-brief.md` §0.1 is explicit that the lottery itself is not being touched: `runLottery`,
 * `applyToiletBowlPriority` and `LOTTERY_ODDS` keep their signatures and their answers, and no
 * component does arithmetic. But the reveal (§3) asks two questions the lottery has never had to
 * answer, because they are about WATCHING a result rather than producing one:
 *
 *   Which positions are filled at this instant?  The draw is finished the moment it is recorded;
 *   the reveal is a clock reading of one stored timestamp, so every GM's screen shows the same
 *   row landing at the same second without anything being broadcast per step.
 *
 *   What is left for me?  "Six names left, and you hold 20% of what remains" is the line the brief
 *   says GMs will actually watch, and it has to be recomputed from the UNDRAWN pool at each step
 *   rather than printed off the odds table.
 *
 * Both live here rather than in the component, for the same reason the rest of this app keeps its
 * numbers out of JSX: a percentage computed in a render is a percentage nobody can test.
 *
 * THE POSITIONS FILL FROM THE BOTTOM. Position 10 is announced first and position 1 last — that is
 * what the design draws (rows 7-10 filled, 1-6 still dashed, "4 of 10 drawn") and what the brief
 * means by "a GM watches the empty rows above him shrink".
 */

import { LOTTERY_ODDS } from './draftOrder.js';

/**
 * Cadence, WEIGHTED TO INFORMATION (Pass 06 §5c).
 *
 * It used to be two speeds: a steady 3.6s down the board and a 9s stall for the last three. That
 * spends the same time on the row nobody is watching as on the row where the whole thing turns.
 *
 *   POSITION 10 IS THE CHAMPION ABOUT 93% OF THE TIME. It is the lowest weight in the pool and it
 *   falls out of the draw first almost every year — the opening card is a formality, and it was
 *   getting as long as position 6.
 *
 *   POSITION 6 IS WHERE THE SWING IS. The odds panel asks "still alive for a top-four pick" all
 *   the way down the chalk tail, and that number moves hardest as the tail runs out — the last two
 *   eliminations before the field is fixed are the two that change everybody's answer.
 *
 *   POSITION 4 IS THE LEAST DRAMATIC OF THE FOUR. Once position 5 lands the set of four is known;
 *   naming the worst of them is the announcement with the least left in it.
 *
 *   POSITION 1 KEEPS ITS OWN BEAT — it is never folded into position 2 — but the beat is short.
 *   It is deducible the moment position 2 lands, because three of four placed forces the fourth,
 *   so the wait in front of it is suspense over something the room has already worked out. 3.6s
 *   rather than 9 (Tim, 26 August). What it must not get is a suspense NUMBER that already reads
 *   100%, which is the panel's job, not the clock's.
 *
 * Ten names come in at 50.4 seconds — 55.8 before that last cut, 52.2 before positions 6 and 5
 * were lengthened out of 10 and 9.
 */
export const REVEAL_STEP_MS = 3600;
export const REVEAL_FINALE_MS = 9000;
/** Positions at or under this one get the long pause: 3rd, 2nd and 1st choice. */
export const REVEAL_FINALE_FROM = 3;
/** The chalk formalities at the bottom of the board. */
export const REVEAL_CHALK_MS = 1800;
/** The two eliminations that decide the field. */
export const REVEAL_SWING_MS = 6000;

/**
 * The gap between the second choice landing and the first — cut 60% from `REVEAL_FINALE_MS` on
 * Tim's ask, 26 August.
 *
 * The two ARE separate reveals and always have been; what changed is only the wait between them.
 * The reasoning below still stands — the room wants the moment for first choice — but the moment
 * it wants is the name appearing, not the nine seconds of dead air in front of it. By the time
 * second choice lands, first is not a question any more: three of the four are placed and the
 * fourth is forced, so the panel is already reading 100% and everybody in the room has worked it
 * out. Nine seconds of suspense over a fact is the clock arguing with the arithmetic.
 */
export const REVEAL_LAST_MS = 3600;

/**
 * The bands, in board order. Written as a table rather than a chain of ternaries because the
 * argument for each one is a different argument and each deserves somewhere to live.
 */
export const REVEAL_BANDS: ReadonlyArray<{ from: number; to: number; ms: number; why: string }> = [
  { from: 10, to: 9, ms: REVEAL_CHALK_MS, why: 'chalk — position 10 is the champion ~93% of the time' },
  { from: 8, to: 7, ms: REVEAL_STEP_MS, why: 'the tail starts to cost somebody something' },
  { from: 6, to: 5, ms: REVEAL_SWING_MS, why: 'the largest swing in alive-for-top-four' },
  { from: 4, to: 4, ms: REVEAL_SWING_MS, why: 'the four are known by now; this is the worst of them' },
  { from: 3, to: 2, ms: REVEAL_FINALE_MS, why: 'the two anybody is still watching for' },
  { from: 1, to: 1, ms: REVEAL_LAST_MS, why: 'first choice is forced once second lands — the room wants the name, not the wait' },
];

export const stepMsFor = (position: number): number =>
  REVEAL_BANDS.find((b) => position <= b.from && position >= b.to)?.ms ?? REVEAL_STEP_MS;

/**
 * Milliseconds from the start at which each name lands, in the order they land — so index 0 is
 * the last choosing position and the final entry is the winner.
 */
export function revealSchedule(teams: number): number[] {
  const out: number[] = [];
  let at = 0;
  for (let i = 0; i < teams; i += 1) {
    at += stepMsFor(teams - i);
    out.push(at);
  }
  return out;
}

export const revealDurationMs = (teams: number): number => revealSchedule(teams).at(-1) ?? 0;

/* -- when the broadcast stops being a broadcast ------------------------------------------------ */

/**
 * HOW LONG THE REVEAL KEEPS TAKING THE PAGE, and why it has to stop.
 *
 * `lotteryDrawnAt` is a broadcast: the moment it appears, every subscribed screen drops what it
 * is doing and plays the draw. That is exactly right for the fifty seconds it is happening and
 * for the GM who opens his phone twenty minutes later.
 *
 * It was not right five days later. The dismissal lived in component state, so it did not
 * survive a reload — and the 2026 draw was RECORDED after the fact with the timestamp of the
 * night the room actually watched it, which meant every GM's `Order and lottery` tab opened onto
 * a full-screen replay of an event they had all already seen, every single time, forever. Tim,
 * 31 August: "the lottery screen shouldn't still be loading by default since the lottery is
 * over."
 *
 * A DAY IS THE WINDOW. Long enough that anybody who was asleep for the draw still gets the
 * moment; short enough that the page goes back to being a page. After it, the board is history
 * and the ledger already tells it — with `Replay the draw` for anyone who wants it back.
 */
export const REVEAL_STANDS_MS = 24 * 60 * 60 * 1000;

export interface RevealGate {
  /** `draftState.lotteryDrawnAt`. */
  drawnAt: string | null | undefined;
  /** The order has all ten names — a half-written draw is not a broadcast. */
  complete: boolean;
  /** This browser has already watched THIS draw. Persisted, not component state. */
  seen: boolean;
  now: number;
}

/** Does the reveal take the page on its own? A replay is the caller's business, not this. */
export function revealTakesPage(i: RevealGate): boolean {
  if (!i.drawnAt || !i.complete || i.seen) return false;
  const at = Date.parse(i.drawnAt);
  if (!Number.isFinite(at)) return false;
  /* A clock-skewed future stamp is not a reason to withhold a live draw. */
  return i.now - at < REVEAL_STANDS_MS;
}

/** How many names have landed after `elapsedMs`. Clamped at both ends. */
export function drawnCount(elapsedMs: number, teams: number): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  return revealSchedule(teams).filter((at) => elapsedMs >= at).length;
}

/** Filling from the bottom: after `drawn` steps the last `drawn` positions are the ones showing. */
export const isDrawn = (position: number, drawn: number, teams: number): boolean =>
  position > teams - drawn;

/* -- the four the lottery actually draws -------------------------------------------------------- */

/**
 * 2014-II-1 §2: only the TOP FOUR choosing positions come out of the draw. Five through ten follow
 * the previous season's finish, worst first, and the champion and runner-up drop to the back (§3).
 *
 * So the board has two halves that mean different things, and until now it drew them identically.
 * The medals are not decoration — they are the line between "this was won" and "this was earned
 * last season", which is the one thing about the lottery a new manager never knows.
 */
export type Medal = 'gold' | 'silver' | 'bronze' | 'blue';

const MEDALS: readonly Medal[] = ['gold', 'silver', 'bronze', 'blue'];

/** How many positions the draw itself decides. */
export const DRAWN_POSITIONS = MEDALS.length;

/** The medal a position carries, or null for the six that follow the standings. */
export const medalFor = (position: number): Medal | null =>
  (position >= 1 && position <= DRAWN_POSITIONS ? MEDALS[position - 1]! : null);

/** Where the rule's line falls: after this position the order is last season's, not the draw's. */
export const isLotteryPosition = (position: number): boolean => medalFor(position) !== null;

/* -- weights ---------------------------------------------------------------------------------- */

/**
 * A GM's lottery weight, read off `LOTTERY_ODDS` by where he finished. Never a literal: §0.1 and
 * §6 both say the odds come from that map and nowhere else.
 */
export function weightOf(finishOrder: readonly string[], gm: string): number | null {
  const i = finishOrder.findIndex((g) => g.toUpperCase() === gm.toUpperCase());
  return i < 0 ? null : (LOTTERY_ODDS[i + 1] ?? 0);
}

export interface Standing {
  /** Where the viewer chooses, 1-based, once his name has landed. Null while he is still in. */
  position: number | null;
  /** Names still to come, the viewer included. */
  left: number;
  /** His share of the undrawn weight, as a percentage. Null when he is not in the draw. */
  share: number | null;
}

/**
 * Where the viewer stands after `drawn` names.
 *
 * `share` is his weight over the weight of everyone NOT yet drawn — which is why it climbs as the
 * board fills, and why it is not the number on the odds chart. The design's mock says 20% beside a
 * board with four names already down; the honest figure there is 23%, and the brief settles it:
 * "Recompute the remaining share from the undrawn pool each step."
 */
export function standingAfter(
  { selectionOrder, finishOrder, drawn, teams, me }: {
    selectionOrder: readonly string[];
    finishOrder: readonly string[];
    drawn: number;
    teams: number;
    me: string;
  },
): Standing {
  const at = selectionOrder.findIndex((g) => g.toUpperCase() === me.toUpperCase());
  const position = at < 0 ? null : at + 1;
  const left = Math.max(0, teams - drawn);
  const placed = position !== null && isDrawn(position, drawn, teams);

  if (placed || position === null) return { position: placed ? position : null, left, share: null };

  const undrawn = selectionOrder.slice(0, left);
  const pool = undrawn.reduce((sum, g) => sum + (weightOf(finishOrder, g) ?? 0), 0);
  const mine = weightOf(finishOrder, me);
  const share = mine === null || pool <= 0 ? null : Math.round((mine / pool) * 100);
  return { position: null, left, share };
}

/* -- the standing note ------------------------------------------------------------------------ */

const WORDS = [
  'No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
];

/** A count as the design writes it — "Six names left", not "6 names left". */
export const countWord = (n: number): string => WORDS[n] ?? String(n);

export const ordinal = (n: number): string => (
  n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd'
    : n % 10 === 3 && n !== 13 ? 'rd' : 'th'
);

/**
 * The bar at the foot of the reveal.
 *
 * Two states, and the second one is Tim's, 21 August: the board holds on the finished ten rather
 * than dropping anybody back to the ranking, so once a GM's name has landed the bar stops telling
 * him what he still holds and tells him what he got.
 */
export function standingNote(s: Standing, teams: number): string {
  if (s.position !== null) {
    return `You choose ${s.position}${ordinal(s.position)} of ${teams}.`;
  }
  if (s.share === null) return 'You are not in this draw.';
  /*
   * NO PERCENTAGE HERE ANY MORE (Pass 06 §5a).
   *
   * This used to end "and you hold 11% of what remains" — his share of the undrawn WEIGHT. The
   * odds panel now sits directly above it answering "still alive for a top-four pick", and for the
   * same GM at the same moment those two read 11% and 75%. Both are true and they are answers to
   * different questions, which is precisely the confusion §5a exists to stop: a GM scanning for
   * "how am I doing" finds two numbers about himself and takes the worse one as the answer.
   *
   * The panel keeps the number because it is the honest one. This keeps the fact the panel does
   * not carry — that he is still in, and how much board is left. `Standing.share` survives because
   * it is still what the panel-less rehearsal and the tests read.
   */
  return `You are still in. ${countWord(s.left)} name${s.left === 1 ? '' : 's'} left.`;
}

/* -- the seed, as the commissioner sees it ---------------------------------------------------- */

/**
 * The seed is a number — `seededRng` takes one and the whole point of §5 is that anyone can replay
 * the draw from it — but a bare 1,900,000,000 is not something nine people in a room can read off
 * a television and check afterwards. The design writes it `2026-08-31-a7f3c9`: the date it was
 * drawn, then the number in hex. Display only; the stored value is still the integer.
 */
export function seedLabel(seed: number, drawnAt: string | number | Date): string {
  const d = new Date(drawnAt);
  const day = Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  return `${day}-${(seed >>> 0).toString(16).padStart(6, '0')}`;
}
